import {
  Box,
  Button,
  Checkbox,
  Stack,
  TextField,
  Tooltip,
  Typography,
  NumericInput,
} from '@atgfr8/atg-ui-tailwind';
import { TrackingProviders } from '@common/order/types';
import {
  BasicModal,
  CautionInput,
  Input,
  RadioGroup,
  PhoneInput,
} from '@components';
import { CautionPhoneInput } from '@components/PhoneInput/CautionPhoneInput';
import { HoverPop } from '@components/HoverPop';
import { FeatureFlags } from '@constants/featureFlags';
import { createAppStore } from '@store/createAppStore';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  fetchReferenceNumbers,
  ltlReferenceNumberStore,
  updateReferenceNumberStore,
} from '@store/ltl/store';
import {
  LTLReferenceNumber,
  LTLReferenceNumberTypes,
  LTLTrackingData,
} from '@store/ltl/types';
import { closeModal, openModal } from '@store/modals';
import {
  orderStore,
  totalLoads,
  updateLoadPropertyAtIndex,
  validateFieldForOrder,
} from '@store/orders';
import { userStore } from '@store/user';
import {
  CloseCircleIcon,
  FileDocumentEditIcon,
  SecurityIcon,
  AlertIcon,
} from '@atgfr8/icons';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { normalizeProNumber } from '@utils/ltlUtils';
import { isAdmin, isSuperAdmin } from '@utils/utils';
import { AcceptTrackingModal } from '@views/order/components/models';
import { ACCEPT_TRACKING_CHARGES_ID } from '@views/order/constants';
import { createEffect, createSignal, Show } from 'solid-js';

import { isLoadDispatched } from '../../ltlQuote/helpers';
import { CancelInsuranceCbModal } from './CancelInsuranceCbModal';
import { CancelInsuranceModal } from './CancelInsuranceModal';
import { InsuranceModal } from './InsuranceModal';
import {
  cancelInsuranceCbModal,
  cancelInsuranceModal,
  freightValue,
  insuranceModal,
  isCarrierActive,
  isLoadReady,
  isLoadSureVendorAttached,
  loadId,
  requoteModal,
} from './LoadSureHelpers';
import { PhoneWarning } from './PhoneWarning';
import { RequoteModal } from './RequoteModal';
import { canOverrideCarrierHistory, getTotalWeightAndUnit } from './utils';

type PropsT = {
  load: ILoadEditViewModel;
  tabIndex: number;
  trackingData: LTLTrackingData | null;
};

// eslint-disable-next-line complexity
export const DriverDetails = (props: PropsT) => {
  const [referenceNumbers, setReferenceNumbers] = createAppStore(
    new Array<LTLReferenceNumber>(),
    'driverDetailsReferenceNumbers',
    true,
  );
  const [isDriverDetailsDirty, setIsDriverDetailsDirty] = createSignal(false);
  createEffect(() => {
    if (orderStore.resetDirty) {
      setIsDriverDetailsDirty(false);
    }
  });

  createEffect(async () => {
    if (props.load.id && !ltlReferenceNumberStore.isSaving) {
      setReferenceNumbers([]);
      await fetchReferenceNumbers(props.load.id, (refNumbers) =>
        setReferenceNumbers(refNumbers),
      );
    }
  });

  const showSecondaryDriver = () => {
    return props.load.mode !== 'LTL' && props.load.teamRequired == true;
  };

  const handleRadioChange = (value: string) => {
    updateLoadPropertyAtIndex({
      cargoValueIsHigh: value,
    });
  };

  const isPickupDisabled = (
    referenceNumber: LTLReferenceNumber,
    loadStatus: string | undefined,
  ) => {
    return (
      referenceNumber.type === LTLReferenceNumberTypes.Pickup &&
      !isLoadDispatched(loadStatus ?? '')
    );
  };

  const isProReferenceNumber = (referenceNumber: LTLReferenceNumber) =>
    referenceNumber.type === LTLReferenceNumberTypes.Pro ||
    referenceNumber.type === LTLReferenceNumberTypes.ProNumber;

  // Normalize a pro number on blur so stored values stay consistent
  // (trimmed, unprefixed, single value). No-op if nothing changed.
  const normalizeProReferenceNumber = (
    referenceNumber: LTLReferenceNumber,
    index: number,
  ) => {
    if (!isProReferenceNumber(referenceNumber)) return;

    const normalized = normalizeProNumber(referenceNumber.value);
    if (normalized === referenceNumber.value) return;

    setReferenceNumbers(index, 'value', normalized);
    updateReferenceNumberStore(referenceNumber.type, normalized);
  };

  const acceptTrackingModalId = `${ACCEPT_TRACKING_CHARGES_ID}-${props.tabIndex}-modal`;

  return (
    <Box flex={1} textAlign="left" class="min-w-0 pr-1">
      <Box mb={1}>
        <Typography
          variant="body1"
          class="text-base font-normal text-[#123B50] tracking-[0.00938em] text-armstrong-primary"
        >
          Driver Details
        </Typography>
      </Box>
      <Stack gap={0.25}>
        <Box class="[&>div>input]:rounded-xs [&>div>input]:bg-white">
          <Input
            label="Primary Driver Name"
            placeholder="Enter Driver Name"
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[`loads[${props.tabIndex}].driver`]
            }
            value={props.load.driver ?? ''}
            onChange={(e: { target: { value: string } }) => {
              updateLoadPropertyAtIndex({
                driver: e.target.value,
              });
            }}
            disabled={orderStore.isReadOnly}
          />
        </Box>
        <Box class="[&>div>input]:rounded-xs [&>div>input]:bg-white">
          <Show
            when={
              Boolean(
                props.load.trackingServiceId === TrackingProviders.TruckerTools,
              ) && isDriverDetailsDirty()
            }
          >
            <PhoneWarning />
          </Show>
          <CautionPhoneInput
            resetDirty={orderStore.resetDirty}
            caution={Boolean(
              props.load.trackingServiceId === TrackingProviders.TruckerTools,
            )}
            onInput={() => {
              setIsDriverDetailsDirty(true);
            }}
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[
                `loads[${props.tabIndex}].driverPhoneNumber`
              ]
            }
            placeholder="Enter Driver Phone"
            label="Primary Driver Phone"
            value={props.load.driverPhoneNumber ?? ''}
            onChange={(value) => {
              updateLoadPropertyAtIndex({
                driverPhoneNumber: value === '' ? null : value,
              });
            }}
            onBlur={async () => {
              await validateFieldForOrder(
                `loads.${props.tabIndex}.driverPhoneNumber`,
                `loads[${props.tabIndex}].driverPhoneNumber`,
              );
            }}
            disabled={orderStore.isReadOnly}
            dataTestId="primary-driver-phone-phoneInput"
          />
          <Show
            when={
              props.load.id > 0 &&
              (Boolean(props.load.driverPhoneNumber) ||
                Boolean(props.load.truckNumber) ||
                props.load.trailerNumber)
            }
          >
            <div class="mt-1">
              <Checkbox
                label="Enable Tracking"
                checked={orderStore.order.loads[props.tabIndex].trackingEnabled}
                onChange={(prevValue) => {
                  if (
                    prevValue ||
                    props.load.trackingServiceId ===
                      TrackingProviders.TruckerTools
                  ) {
                    openModal(acceptTrackingModalId);
                  }
                  if (
                    !Boolean(
                      orderStore.order.loads[props.tabIndex].trackingServiceId,
                    )
                  ) {
                    updateLoadPropertyAtIndex({
                      trackingEnabled:
                        !orderStore.order.loads[props.tabIndex].trackingEnabled,
                    });
                  }
                }}
                disabled={
                  !(
                    props.load.driverPhoneNumber !== '' ||
                    (props.load.truckNumber?.trim().length ?? 0) > 0 ||
                    (props.load.trailerNumber?.trim().length ?? 0) > 0
                  ) || orderStore.isReadOnly
                }
                dataTestId="enable-tracking-checkboxInput"
                size="xs"
                color="info"
                class="ml-[-9px]"
              />
              <BasicModal
                id={acceptTrackingModalId}
                title="Enable Tech Tracking"
                width={'90vw'}
                onClose={() => {
                  closeModal(acceptTrackingModalId);
                }}
                footer={false}
                showClose={true}
              >
                <AcceptTrackingModal
                  load={props.load}
                  modalId={acceptTrackingModalId}
                  tabIndex={props.tabIndex}
                  setTracking={(e: boolean) =>
                    updateLoadPropertyAtIndex({
                      trackingEnabled: e,
                    })
                  }
                />
              </BasicModal>
            </div>
          </Show>
        </Box>
        <Show when={showSecondaryDriver()}>
          <Stack gap={0.25}>
            <Box class="my-1 [&>div>input]:rounded-xs [&>div>input]:bg-white">
              <TextField
                label="Secondary Driver Name"
                placeholder="Second Driver"
                value={props.load.secondDriver ?? ''}
                maxLength={30}
                onChange={(value: string) => {
                  updateLoadPropertyAtIndex({
                    secondDriver: value,
                  });
                }}
                disabled={orderStore.isReadOnly}
                dataTestId="secondary-driver-name-textInput"
              />
            </Box>
            <Box class="my-1 [&>div>input]:rounded-xs [&>div>input]:bg-white">
              <PhoneInput
                label="Secondary Driver Phone"
                placeholder="Second Driver Phone #"
                value={props.load.secondDriverPhoneNumber ?? ''}
                onChange={(value) => {
                  updateLoadPropertyAtIndex({
                    secondDriverPhoneNumber: value,
                  });
                }}
                disabled={orderStore.isReadOnly}
                dataTestId="secondary-driver-phone-phoneInput"
              />
            </Box>

            <Box>
              <Tooltip
                title="Tracking will only be available for the Primary Driver's phone number"
                placement="bottom"
                class="z-10"
                dataTestId={formatDataTestId('Tracking Notice', 'div')}
              >
                <Typography
                  component={'p'}
                  variant="body2"
                  class="flex items-center gap-1"
                >
                  <AlertIcon class="text-[16px] text-[#ccb10e] shrink-0" />
                  Tracking Notice
                </Typography>
              </Tooltip>
            </Box>
          </Stack>
        </Show>
        <Box class="[&>div>input]:rounded-xs [&>div>input]:bg-white">
          <CautionInput
            caution={Boolean(
              props.load.trackingServiceId === TrackingProviders.TruckerTools,
            )}
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[`loads[${props.tabIndex}].truckNumber`]
            }
            disabled={orderStore.isReadOnly}
            resetDirty={orderStore.resetDirty}
            label="Truck Number"
            value={props.load.truckNumber ?? ''}
            placeholder="Enter Truck Number"
            onInput={() => {
              setIsDriverDetailsDirty(true);
            }}
            onChange={(e: { target: { value: string } }) => {
              updateLoadPropertyAtIndex({
                truckNumber: e.target.value,
              });
            }}
          />
        </Box>
        <Box class="[&>div>input]:rounded-xs [&>div>input]:bg-white">
          <CautionInput
            caution={Boolean(
              props.load.trackingServiceId === TrackingProviders.TruckerTools,
            )}
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[
                `loads[${props.tabIndex}].trailerNumber`
              ]
            }
            disabled={orderStore.isReadOnly}
            placeholder="Enter Trailer Number"
            resetDirty={orderStore.resetDirty}
            label="Trailer Number"
            value={props.load.trailerNumber ?? ''}
            onChange={(e: { target: { value: string } }) => {
              updateLoadPropertyAtIndex({
                trailerNumber: e.target.value,
              });
            }}
            onInput={() => {
              setIsDriverDetailsDirty(true);
            }}
          />
        </Box>
        <Box class="[&>div>input]:rounded-xs [&>div>input]:bg-white">
          <Input
            label="VIN Number"
            placeholder="Enter VIN Number"
            error={
              orderStore.orderFormError &&
              orderStore.orderFormError[`loads[${props.tabIndex}].vinNumber`]
            }
            maxChar={50}
            hideMaxCharCount={true}
            value={props.load.vinNumber ?? ''}
            onChange={(e: { target: { value: string } }) => {
              updateLoadPropertyAtIndex({
                vinNumber: e.target.value.replace(/[^a-zA-Z0-9]/g, ''),
              });
            }}
            onInput={(e: { target: { value: string } }) => {
              const sanitized = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
              if (sanitized !== e.target.value) {
                e.target.value = sanitized;
              }
            }}
            disabled={orderStore.isReadOnly}
          />
        </Box>
        <Show when={props.load.mode === 'LTL'}>
          {referenceNumbers
            .sort((a, b) => a.sequence - b.sequence)
            .map((referenceNumber, index) => (
              <Box
                class="[&>div>input]:rounded-xs [&>div>input]:bg-white"
                style={{ 'margin-top': index > 0 ? '8px' : '6px' }}
              >
                <TextField
                  type="text"
                  size="sm"
                  label={referenceNumber.displayName}
                  value={referenceNumber.value}
                  onChange={(value: string) => {
                    const newValue = value;
                    setReferenceNumbers(index, 'value', newValue);
                    updateReferenceNumberStore(referenceNumber.type, newValue);
                  }}
                  on:blur={() =>
                    normalizeProReferenceNumber(referenceNumber, index)
                  }
                  disabled={
                    !referenceNumber.modifiable ||
                    isPickupDisabled(referenceNumber, props.load.status)
                  }
                  dataTestId={`${referenceNumber.displayName.replace(/\s+/g, '-').replace(/:/g, '')}-textInput`}
                />
              </Box>
            ))}
        </Show>
        <Show when={props.load.mode !== 'TL'}>
          <Box style={{ 'margin-top': '8px' }}>
            <NumericInput
              label="Freight Value ($)"
              style={{ 'background-color': 'white' }}
              error={
                orderStore.orderFormError &&
                orderStore.orderFormError[
                  `loads[${props.tabIndex}].freightValue`
                ]
              }
              value={props.load.freightValue ?? ''}
              onChange={(v: string) => {
                if (v !== '') {
                  updateLoadPropertyAtIndex({
                    freightValue: Number(v),
                  });
                } else {
                  updateLoadPropertyAtIndex({
                    freightValue: undefined,
                  });
                }
              }}
            />
          </Box>
          <HoverPop
            anchor={
              <Show
                when={
                  isFeatureFlagEnabled(FeatureFlags.LoadSure)() &&
                  !isLoadSureVendorAttached() &&
                  !orderStore.isCreate &&
                  props.load.mode !== 'LTL' &&
                  props.load.mode !== 'Ocean' &&
                  props.load.mode !== 'Air' &&
                  loadId() !== 0 &&
                  isCarrierActive() &&
                  isLoadReady()
                }
              >
                <div class="w-full">
                  <Button
                    dataTestId={formatDataTestId('Get Insurance', 'button')}
                    variant="contained"
                    color="primary"
                    class="w-full"
                    startIcon={<SecurityIcon />}
                    disabled={
                      Number(freightValue()) > 1000000 ||
                      !Boolean(freightValue()) ||
                      props.load.equipment === 'PO, Power Only'
                    }
                    onClick={() => {
                      openModal(insuranceModal);
                    }}
                  >
                    Get Insurance
                  </Button>
                </div>
              </Show>
            }
            render={!Boolean(freightValue())}
          >
            <div class="text-armstrong-sm p-4">Enter a Cargo Value amount</div>
          </HoverPop>
          <Show
            when={
              isFeatureFlagEnabled(FeatureFlags.LoadSure)() &&
              isLoadSureVendorAttached() &&
              !orderStore.isCreate &&
              props.load.mode !== 'LTL' &&
              isLoadReady()
            }
          >
            <div class="w-full flex flex-col gap-y-2">
              <Button
                dataTestId={formatDataTestId('Requote', 'button')}
                variant="contained"
                class="w-full bg-[#248670]!"
                startIcon={<FileDocumentEditIcon size={20} />}
                onClick={() => {
                  openModal('requoteModal');
                }}
              >
                Requote
              </Button>
              <Button
                dataTestId={formatDataTestId('Cancel Insurance', 'button')}
                variant="contained"
                class="w-full bg-[#b00020]!"
                startIcon={<CloseCircleIcon size={20} />}
                onClick={() => {
                  openModal('cancelInsuranceModal');
                }}
              >
                Cancel Insurance
              </Button>
            </div>
          </Show>
        </Show>
        <Show when={props.load.mode === 'TL'}>
          <Box>
            <Box>
              <Typography component={'p'} variant="body2">
                Is the cargo value more than $100,000?
              </Typography>
              <RadioGroup
                direction="horizontal"
                value={props.load.cargoValueIsHigh}
                options={[
                  { label: 'Yes', value: 'Yes' },
                  { label: 'No', value: 'No' },
                ]}
                onChange={handleRadioChange}
                disabled={orderStore.isReadOnly}
              />
            </Box>
            <Box>
              <NumericInput
                disabled={orderStore.isReadOnly}
                label={
                  props.load.cargoValueIsHigh === 'Yes'
                    ? 'Cargo Value (required)'
                    : 'Cargo Value (optional)'
                }
                error={
                  orderStore.orderFormError &&
                  orderStore.orderFormError[
                    `loads[${props.tabIndex}].freightValue`
                  ]
                }
                startIcon="$"
                // startIcon={<CurrencyUsdIcon size={18} fill="#444" />}
                value={props.load.freightValue ?? ''}
                onChange={(val: unknown) => {
                  if (val !== '') {
                    updateLoadPropertyAtIndex({
                      freightValue: Number(val),
                    });
                  } else {
                    updateLoadPropertyAtIndex({
                      freightValue: undefined,
                    });
                  }
                }}
                on:blur={() =>
                  validateFieldForOrder(
                    `loads.${props.tabIndex}.freightValue`,
                    `loads[${props.tabIndex}].freightValue`,
                  )
                }
                class="w-full"
                dataTestId="cargo-value-textInput"
              />
            </Box>
            <HoverPop
              anchor={
                <Show
                  when={
                    isFeatureFlagEnabled(FeatureFlags.LoadSure)() &&
                    !isLoadSureVendorAttached() &&
                    !orderStore.isCreate &&
                    loadId() !== 0 &&
                    isCarrierActive() &&
                    isLoadReady()
                  }
                >
                  <div class="my-2 w-full">
                    <Button
                      dataTestId={formatDataTestId('Get Insurance', 'button')}
                      variant="contained"
                      color="primary"
                      class="w-full"
                      startIcon={<SecurityIcon size={20} />}
                      disabled={
                        Number(freightValue()) > 1000000 ||
                        freightValue() === undefined ||
                        freightValue() === null ||
                        props.load.equipment === 'PO, Power Only'
                      }
                      onClick={() => {
                        openModal(insuranceModal);
                      }}
                    >
                      Get Insurance
                    </Button>
                  </div>
                </Show>
              }
              render={!Boolean(freightValue())}
            >
              <div class="text-armstrong-sm p-4">
                Enter a Cargo Value amount
              </div>
            </HoverPop>
            <Show
              when={
                isFeatureFlagEnabled(FeatureFlags.LoadSure)() &&
                isLoadSureVendorAttached() &&
                !orderStore.isCreate &&
                isLoadReady()
              }
            >
              <div class="w-full flex flex-col gap-y-2 my-2">
                <Button
                  dataTestId={formatDataTestId('Requote', 'button')}
                  variant="contained"
                  class="w-full bg-[#248670]!"
                  startIcon={<FileDocumentEditIcon size={20} />}
                  onClick={() => {
                    openModal(requoteModal);
                  }}
                >
                  Requote
                </Button>
                <Button
                  dataTestId={formatDataTestId('Cancel Insurance', 'button')}
                  variant="contained"
                  class="w-full bg-[#b00020]!"
                  startIcon={<CloseCircleIcon size={20} />}
                  onClick={() => {
                    openModal(cancelInsuranceModal);
                  }}
                >
                  Cancel Insurance
                </Button>
              </div>
            </Show>
            <Show
              when={props.load.equipment !== 'PO, Power Only'}
              fallback={
                <Box class="text-armstrong-sm!">
                  <Box fontWeight="bold" color="#9ea0a1">
                    Power Only Load - Action Required
                  </Box>
                  <Box>
                    <span class="text-[#9ea0a1]">
                      This load is a power only and requires specifc insurance
                      for non-owned trailers. Please contact{' '}
                    </span>
                    <a
                      href="mailto:claims@armstrongtransport.com"
                      class="text-armstrong-blue cursor-pointer"
                    >
                      claims@armstrongtransport.com
                    </a>{' '}
                    <span class="text-[#9ea0a1]">
                      to purchase this coverage.
                    </span>
                  </Box>
                </Box>
              }
            >
              <Box class="text-armstrong-sm!">
                <span class="text-[#000000] opacity-40">
                  Please confirm the contacted carrier has adequate cargo
                  insurance. Contact{' '}
                </span>
                <a
                  href="mailto:claims@armstrongtransport.com"
                  class="text-armstrong-blue cursor-pointer"
                >
                  claims@armstrongtransport.com
                </a>{' '}
                <span class="text-[#000000] opacity-40">
                  for excess cargo coverage.
                </span>
              </Box>
            </Show>
          </Box>
        </Show>
        <Show
          when={
            (isAdmin() || isSuperAdmin() || canOverrideCarrierHistory()) &&
            props.load.mode == 'TL' &&
            props.load.cargoValueIsHigh === 'Yes' &&
            totalLoads() <= 3
          }
        >
          <Box>
            <Checkbox
              label="Carrier History Override"
              checked={props.load.enableLoadCarrierRestrictionOverride}
              onChange={(val) => {
                updateLoadPropertyAtIndex({
                  enableLoadCarrierRestrictionOverride: val,
                });
              }}
              disabled={orderStore.isReadOnly}
              dataTestId="carrier-history-override-checkboxInput"
              size="xs"
              color="info"
              class="ml-[-9px]"
            />
            <Show when={!(isAdmin() || isSuperAdmin())}>
              <Box style={{ 'max-width': '400px' }}>
                <Typography
                  variant="body1"
                  component={'div'}
                  sxProps={{
                    fontFamily: 'Roboto',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: '400',
                    lineHeight: '166%',
                    letterSpacing: '0.4px',
                  }}
                >
                  By checking this box, you (
                  {props.load.enableLoadCarrierRestrictionOverrideOffice ??
                    userStore.user.office}
                  ) agree to assume 100% liability instead of the typical 70%
                  financial responsibility related to the use of this carrier.
                  This includes, but is not limited to, costs incurred due to
                  damage or loss of goods during transit.
                </Typography>
              </Box>
            </Show>
          </Box>
        </Show>
        <div class="text-base font-bold">
          Net Weight{' '}
          {getTotalWeightAndUnit(props.load, (weightString) => {
            updateLoadPropertyAtIndex({
              weightString,
            });
          })}
        </div>
      </Stack>
      <BasicModal
        id={requoteModal}
        width="97vw"
        maxWidth="600px"
        header={false}
        footer={false}
      >
        <RequoteModal />
      </BasicModal>
      <BasicModal
        id={insuranceModal}
        width="97vw"
        maxWidth="1200px"
        header={false}
        footer={false}
      >
        <InsuranceModal />
      </BasicModal>
      <BasicModal
        id={cancelInsuranceModal}
        width="97vw"
        maxWidth="450px"
        header={false}
        footer={false}
      >
        <CancelInsuranceModal />
      </BasicModal>
      <BasicModal
        id={cancelInsuranceCbModal}
        width="97vw"
        maxWidth="550px"
        header={false}
        footer={false}
        hideClose={true}
      >
        <CancelInsuranceCbModal />
      </BasicModal>
    </Box>
  );
};
